import { Component, inject, DestroyRef, effect } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TokenService } from '../../services/token.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { UserRoleEnum } from '../../enums/user-role.enum';
import { environment } from '../../../environments/environment';
import { MatBadgeModule } from '@angular/material/badge';
import { NotificationsService } from '../../services/notifications/notifications.service';
import { GoogleTranslateService } from '../../services/google-translate.service';

@Component({
  selector: 'heritage-header',
  imports: [MatButtonModule, MatIconModule, MatMenuModule, MatDividerModule, RouterModule, CommonModule, MatBadgeModule],
  templateUrl: './header.html',
  styleUrl: './header.scss',
  standalone: true
})
export class HeaderComponent {
  private router = inject(Router);
  private notificationsService = inject(NotificationsService);
  private tokenService = inject(TokenService);
  private destroyRef = inject(DestroyRef);
  private googleTranslateService = inject(GoogleTranslateService);
  public isTest = !environment.production;
  notificationCounts: any = null;
  currentLanguage = this.googleTranslateService.currentLanguage;
  otherLanguages = this.googleTranslateService.otherLanguages;

  constructor() {
    this.googleTranslateService.load();

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event: NavigationEnd) => {
        this.tokenService.isUserAuthenticated.set(this.tokenService.isAuthenticated());
      });

    effect(() => {
      if (this.notificationsService.notificationCounts()) {
        this.notificationCounts = this.notificationsService.notificationCounts();
      }
    });
  }

  setLanguage(code: string): void {
    this.googleTranslateService.setLanguage(code);
  }

  isLoggedIn(): boolean {
    return this.tokenService.isUserAuthenticated();
  }

  getUserFullName(): string | null {
    return this.tokenService.getFullName();
  }

  getUserEmail(): string | null {
    return this.tokenService.getEmail();
  }

  getUserInitials(): string {
    const fullName = this.getUserFullName();
    if (!fullName) return 'U';

    const names = fullName.trim().split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return names[0][0].toUpperCase();
  }

  isAdmin(): boolean {
    const roleIdsString = localStorage.getItem('roleIds');
    if (!roleIdsString) return false;

    try {
      const roleIds: string[] = JSON.parse(roleIdsString);
      return roleIds.includes(UserRoleEnum.Admin) || roleIds.includes(UserRoleEnum.Expert);
    } catch (error) {
      return false;
    }
  }

  logout(): void {
    this.tokenService.logout();
    this.tokenService.isUserAuthenticated.set(false);
    this.router.navigate(['/']);
  }
}
